import React from 'react';
import {
  AgnesButton,
  AgnesDataTable,
  type AgnesDataTableColumn,
  AgnesDatePickerInput,
  AgnesIconButton,
  AgnesInput,
  AgnesInputFile,
  AgnesLabel,
  AgnesModal,
  AgnesPageHeader,
  AgnesPopover,
  AgnesPopoverContent,
  AgnesPopoverTrigger,
  AgnesSelect,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesTooltip,
} from '@agnes/ui';
import {
  Download,
  FileText,
  Filter,
  History,
  Info,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import { ExportButton } from '@/components/ExportButton';
import { usePodeEditar } from '@/hooks/usePodeEditar';
import MENU_PERMISSIONS from '@/constants/ConstantMenuRoles';
import { useCndtStore } from '@/stores/useCndtStore';
import { confirm } from '@/stores/useConfirm';
import { useEmpregadoresStore } from '@/stores/useEmpregadoresStore';
import { SearchInput } from '@/components/SearchInput';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { countFiltros, labelFiltro } from '@/lib/filtros';
import UtilsMask from '@/utils/UtilsMask';
import ServiceCndt from '@/services/ServiceCndt';
import { notify } from '@/stores/useToast';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

const SELECT_TRIGGER =
  '[&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:flex-1 [&>[data-slot=select-value]]:truncate [&>[data-slot=select-value]]:text-left';

// baixa o recibo da CNDT via URL presigned (erro não estoura — notify)
async function baixarCndt(key: string) {
  try {
    const { data } = await ServiceCndt.downloadUrl(key);
    if (!data?.url) {
      notify.error('Documento indisponível.');
      return;
    }
    window.open(data.url, '_blank');
  } catch {
    notify.error('Não foi possível baixar o documento.');
  }
}

interface Cndt {
  [k: string]: unknown;
  id: number;
  empresa: string;
  cnpj: string;
  emissao: string;
  validade: string;
  codigo: string;
  situacao: string;
  tipo: string;
  totalProcessos: number;
  arquivoCndId: string;
}

// valores de `status` = enum do backend (Cndt.status), label PT-BR na UI.
const SITUACOES = [
  { value: 'REGULAR', label: 'Regular' },
  { value: 'IRREGULAR', label: 'Irregular' },
];

type Any = any;

// modal Adicionar/Editar CNDT — mirror do cadastro manual do CRF/Certidão Federal.
function AdicionarCndt({
  open,
  onClose,
  editar,
}: {
  open: boolean;
  onClose: () => void;
  editar?: Any;
}) {
  const empregadores = useEmpregadoresStore((s) => s.lista);
  const [f, setF] = React.useState({
    empregador: '',
    situacao: '',
    codigo: '',
    emissao: '',
    validade: '',
    arquivoKey: '',
  });
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const irregular = f.situacao === 'IRREGULAR';
  const podeSalvar =
    f.empregador !== '' &&
    f.situacao !== '' &&
    (irregular || (f.codigo !== '' && f.emissao !== '' && f.validade !== ''));

  React.useEffect(() => {
    if (!open) return;
    if (editar) {
      setF({
        empregador: editar.cnpj ?? '',
        situacao: editar._status ?? 'REGULAR',
        codigo: editar.codigo ?? '',
        emissao: editar._emissao ?? '',
        validade: editar._validade ?? '',
        arquivoKey: editar.arquivoCndId ?? '',
      });
    } else {
      setF({ empregador: '', situacao: '', codigo: '', emissao: '', validade: '', arquivoKey: '' });
    }
  }, [editar, open]);

  return (
    <AgnesModal
      open={open}
      title={editar ? 'Editar certidão' : 'Adicionar certidão'}
      maxWidth="md"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-3">
          <AgnesButton variant="text" onClick={onClose}>
            Cancelar
          </AgnesButton>
          <AgnesButton
            variant="primary"
            data-qa="cndt-modal-salvar"
            disabled={!podeSalvar}
            onClick={async () => {
              const dto = {
                cnpj: f.empregador,
                status: f.situacao,
                codigo: f.codigo || undefined,
                dataEmissao: f.emissao || undefined,
                dataValidade: f.validade || undefined,
                arquivoS3Id: f.arquivoKey || undefined,
              };
              const ok = editar?.id
                ? await useCndtStore.getState().atualizar(editar.id, dto)
                : await useCndtStore.getState().salvar(dto);
              if (ok) onClose(); // mantém o modal aberto no erro (preserva o que foi digitado)
            }}
          >
            Salvar
          </AgnesButton>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <AgnesLabel required>Empregador</AgnesLabel>
          <AgnesSelect value={f.empregador} onValueChange={set('empregador')}>
            <AgnesSelectTrigger className={SELECT_TRIGGER}>
              <AgnesSelectValue placeholder="Selecione uma opção" />
            </AgnesSelectTrigger>
            <AgnesSelectContent>
              {empregadores.map((e) => (
                <AgnesSelectItem key={e.cnpjFilial} value={e.cnpjFilial}>
                  {e.company || e.fantasyName || ''}
                </AgnesSelectItem>
              ))}
            </AgnesSelectContent>
          </AgnesSelect>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <AgnesLabel required>Situação</AgnesLabel>
            <AgnesSelect value={f.situacao} onValueChange={set('situacao')}>
              <AgnesSelectTrigger className={SELECT_TRIGGER}>
                <AgnesSelectValue placeholder="Selecione" />
              </AgnesSelectTrigger>
              <AgnesSelectContent>
                {SITUACOES.map((o) => (
                  <AgnesSelectItem key={o.value} value={o.value}>
                    {o.label}
                  </AgnesSelectItem>
                ))}
              </AgnesSelectContent>
            </AgnesSelect>
          </div>

          <AgnesInput
            required
            label="Código"
            hint="Preencha 22 caracteres (A-z, 0-9)"
            value={f.codigo}
            onChange={(e) => set('codigo')(e.target.value.toUpperCase())}
            placeholder="Obrigatório"
            disabled={irregular}
            maxLength={22}
          />

          <AgnesDatePickerInput
            required
            label="Emissão"
            hint="Preencha a data atual ou retroativa"
            value={f.emissao}
            onChange={(value) => set('emissao')(value)}
            placeholder="Obrigatório"
            disabled={irregular}
          />

          <AgnesDatePickerInput
            required
            label="Validade"
            hint="Preencha a data futura"
            value={f.validade}
            onChange={(value) => set('validade')(value)}
            placeholder="Obrigatório"
            disabled={irregular}
          />
        </div>

        <div>
          <p className="mb-2 text-sm text-agnes-text-muted">
            Tamanho máximo do arquivo é 10MB. O tipo de arquivo suportado é .pdf
          </p>
          <AgnesInputFile
            accept="application/pdf"
            disabled={irregular}
            buttonLabel="Adicionar arquivo *"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const cnpjLimpo = (f.empregador || '').replace(/[^a-zA-Z0-9]/g, '');
              const key = `${cnpjLimpo.slice(0, 8)}/certidoes/cndt/manual_${cnpjLimpo}_${Date.now()}.pdf`;
              try {
                await ServiceCndt.uploadArquivo(file, key);
                setF((s) => ({ ...s, arquivoKey: key }));
              } catch {
                notify.error('Falha no upload do arquivo.');
              }
            }}
          />
          {f.arquivoKey && (
            <div
              className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-agnes-border px-3 py-2"
              data-qa="cndt-arquivo-anexado"
            >
              <span className="truncate text-sm text-agnes-text">Documento anexado</span>
              <AgnesButton
                variant="text"
                size="sm"
                startIcon={<Download className="h-4 w-4" />}
                onClick={() => baixarCndt(f.arquivoKey)}
                aria-label="Baixar documento anexado"
                data-qa="cndt-arquivo-anexado-baixar"
              >
                Baixar
              </AgnesButton>
            </div>
          )}
        </div>

        <p className="text-sm font-semibold text-agnes-text">
          * Campos de preenchimento obrigatório
        </p>
      </div>
    </AgnesModal>
  );
}

// popover Filtro — aplica empregador + situação server-side (mesmo padrão do CRF).
function CndtFiltro() {
  const empregadores = useEmpregadoresStore((s) => s.lista);
  const opcoesEmpregador = empregadores.map((e) => ({
    value: e.cnpjFilial,
    label: e.company || e.fantasyName || '',
  }));
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState({ empregador: '', status: '' });
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const filtroAplicado = useCndtStore((s) => s.filtro);
  const nFiltros = countFiltros(filtroAplicado);
  const aplicar = () => {
    useCndtStore.getState().aplicarFiltro(f);
    setOpen(false);
  };
  const limpar = () => {
    setF({ empregador: '', status: '' });
    useCndtStore.getState().limparFiltro();
  };
  return (
    <AgnesPopover open={open} onOpenChange={setOpen}>
      <AgnesPopoverTrigger asChild>
        <AgnesButton
          variant="outlined"
          startIcon={<Filter className="h-4 w-4" />}
          aria-label={nFiltros > 0 ? `Filtro, ${nFiltros} ativos` : 'Filtro'}
        >
          {labelFiltro('Filtro', nFiltros)}
        </AgnesButton>
      </AgnesPopoverTrigger>
      <AgnesPopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-agnes-border px-4 py-3">
          <span className="text-base font-semibold text-agnes-text">Filtro</span>
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="text-agnes-text-muted hover:text-agnes-text"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-col gap-3 px-4 py-4">
          <AgnesSelect value={f.empregador} onValueChange={set('empregador')}>
            <AgnesSelectTrigger className={SELECT_TRIGGER}>
              <AgnesSelectValue placeholder="Empregador" />
            </AgnesSelectTrigger>
            <AgnesSelectContent>
              {opcoesEmpregador.map((o) => (
                <AgnesSelectItem key={o.value} value={o.value}>
                  {o.label}
                </AgnesSelectItem>
              ))}
            </AgnesSelectContent>
          </AgnesSelect>
          <AgnesSelect value={f.status} onValueChange={set('status')}>
            <AgnesSelectTrigger className={SELECT_TRIGGER}>
              <AgnesSelectValue placeholder="Situação" />
            </AgnesSelectTrigger>
            <AgnesSelectContent>
              {SITUACOES.map((o) => (
                <AgnesSelectItem key={o.value} value={o.value}>
                  {o.label}
                </AgnesSelectItem>
              ))}
            </AgnesSelectContent>
          </AgnesSelect>
        </div>
        <div className="flex flex-col gap-2 border-t border-agnes-border px-4 py-3">
          <AgnesButton variant="primary" onClick={aplicar} className="w-full">
            Aplicar filtro
          </AgnesButton>
          <AgnesButton
            variant="outlined"
            onClick={limpar}
            startIcon={<X className="h-4 w-4" />}
            className="w-full"
          >
            Limpar filtro
          </AgnesButton>
        </div>
      </AgnesPopoverContent>
    </AgnesPopover>
  );
}

const CNPJS_MONITORADOS_INFO =
  'As certidões listadas nesta tela são centralizadas no CNPJ matriz, logo, a certidão considera todos os CNPJs que possuam o mesmo CNPJ raiz. Deste modo a certidão abarca matriz e todas as filiais que compartilham o mesmo CNPJ raiz (8 primeiros dígitos do CNPJ são os mesmos).';

function StatCard({
  title,
  value,
  caption,
  accent,
  dataQa,
}: {
  title: React.ReactNode;
  value: number;
  caption: string;
  accent?: boolean;
  dataQa?: string;
}) {
  return (
    <div
      data-qa={dataQa}
      className={`rounded-2xl border border-agnes-border bg-agnes-bg-surface p-5 ${accent ? 'border-t-4 border-t-agnes-success' : ''}`}
    >
      <div className="flex items-start justify-between">
        <p className="text-agnes-text">{title}</p>
        {accent && (
          <AgnesTooltip className="max-w-sm whitespace-normal" content={CNPJS_MONITORADOS_INFO}>
            <Info className="h-4 w-4 shrink-0 text-agnes-primary" />
          </AgnesTooltip>
        )}
      </div>
      <p className="my-2 text-4xl font-bold text-agnes-text">{value}</p>
      <p className="text-sm text-agnes-text-muted">{caption}</p>
    </div>
  );
}

export default function PageCndt() {
  const [syncCnpj, setSyncCnpj] = React.useState('');
  const podeEditar = usePodeEditar(MENU_PERMISSIONS.CNDT_EDITAR);
  const [modal, setModal] = React.useState(false);
  const [editarLinha, setEditarLinha] = React.useState<Any | null>(null);
  const [histId, setHistId] = React.useState<string | null>(null);

  const lista = useCndtStore((s) => s.lista);
  const total = useCndtStore((s) => s.total);
  const page = useCndtStore((s) => s.page);
  const size = useCndtStore((s) => s.size);
  const busca = useCndtStore((s) => s.busca);
  const setBusca = useCndtStore((s) => s.setBusca);
  const sortBy = useCndtStore((s) => s.sortBy);
  const sortOrder = useCndtStore((s) => s.sortOrder);
  const setSort = useCndtStore((s) => s.setSort);
  const dash = useCndtStore((s) => s.dashboard);
  const carregando = useCndtStore((s) => s.carregando);
  const sincronizando = useCndtStore((s) => s.sincronizando);
  const exportando = useCndtStore((s) => s.exportando);
  const historico = useCndtStore((s) => s.historico);
  const empregadores = useEmpregadoresStore((s) => s.lista);
  const { hiddenColumnKeys, toggleColumn } = useColumnPreferences('certidoes-cndt');

  React.useEffect(() => {
    useEmpregadoresStore.getState().buscar();
    useCndtStore.getState().carregar();
    useCndtStore.getState().carregarDashboard();
  }, []);

  const columns: AgnesDataTableColumn<Cndt>[] = [
    {
      key: 'idx',
      header: '',
      width: 48,
      toggleable: false,
      render: (r) => (
        <span className="text-agnes-text-muted">{lista.indexOf(r) + 1 + page * size}</span>
      ),
    },
    { key: 'empresa', header: 'Empregador', width: 280, sortable: true },
    {
      key: 'cnpj',
      header: 'CNPJ',
      width: 170,
      sortable: true,
      render: (r) => UtilsMask.cnpj(r.cnpj),
    },
    { key: 'emissao', header: 'Emissão', width: 130, sortable: true },
    { key: 'validade', header: 'Validade', width: 130, sortable: true },
    { key: 'codigo', header: 'Código de controle', width: 220, sortable: true },
    {
      key: 'tipo',
      header: 'Tipo',
      width: 160,
      sortable: true,
      render: (r) => (
        <span data-qa={`cndt-tipo-${r.id}`} className="block max-w-[140px] truncate" title={r.tipo}>
          {r.tipo}
        </span>
      ),
    },
    { key: 'totalProcessos', header: 'Nº processos', width: 130, align: 'center', sortable: true },
    {
      key: 'acoes',
      header: 'Ações',
      width: 170,
      align: 'center',
      toggleable: false,
      render: (r) => (
        <div className="flex items-center justify-center gap-1">
          <AgnesIconButton
            aria-label="Download"
            data-qa="cndt-acao-download"
            size="sm"
            disabled={!r.arquivoCndId}
            onClick={() => baixarCndt(r.arquivoCndId)}
          >
            <Download className="h-4 w-4" />
          </AgnesIconButton>
          <AgnesIconButton
            aria-label="Histórico"
            data-qa="cndt-acao-historico"
            size="sm"
            onClick={() => {
              setHistId(String(r.id));
              useCndtStore.getState().carregarHistorico(String(r.id));
            }}
          >
            <History className="h-4 w-4" />
          </AgnesIconButton>
          {podeEditar && (
            <>
              <AgnesIconButton
                aria-label="Editar"
                data-qa="cndt-acao-editar"
                size="sm"
                onClick={() => {
                  setEditarLinha(r);
                  setModal(true);
                }}
              >
                <Pencil className="h-4 w-4" />
              </AgnesIconButton>
              <AgnesIconButton
                aria-label="Excluir"
                data-qa="cndt-acao-excluir"
                size="sm"
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Excluir CNDT',
                    message: 'Deseja excluir esta CNDT?',
                    okLabel: 'Excluir',
                    cancelLabel: 'Cancelar',
                    dataQa: 'cndt-excluir-modal',
                  });
                  if (ok) useCndtStore.getState().excluir(String(r.id));
                }}
              >
                <Trash2 className="h-4 w-4" />
              </AgnesIconButton>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <AgnesPageHeader
        icon={<FileText />}
        title="CNDT — Certidão Negativa de Débitos Trabalhistas"
        subtitle="Monitore a regularidade trabalhista"
        breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Certidões' }, { label: 'CNDT' }]}
        actions={
          podeEditar && (
            <AgnesButton
              variant="primary"
              data-qa="cndt-btn-adicionar"
              startIcon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setEditarLinha(null);
                setModal(true);
              }}
            >
              Adicionar
            </AgnesButton>
          )
        }
      />

      <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard
          accent
          dataQa="cndt-card-monitorados"
          title={
            <>
              Total de CNPJs <b>monitorados</b>
            </>
          }
          value={dash?.quantidadeTotalMonitoradas ?? 0}
          caption={`${dash?.percentualTotalMonitoradas ?? 0}% em relação ao total de CNPJs raíz cadastrados`}
        />
        <StatCard
          dataQa="cndt-card-regular"
          title={
            <>
              Certidões <b>regulares</b>
            </>
          }
          value={dash?.quantidadeRegular ?? 0}
          caption={`${dash?.percentualRegular ?? 0}% em relação ao total de CNPJs monitorados`}
        />
        <StatCard
          dataQa="cndt-card-indisponivel"
          title={
            <>
              Certidões <b>indisponíveis</b>
            </>
          }
          value={dash?.quantidadeIndisponivel ?? 0}
          caption={`${dash?.percentualIndisponivel ?? 0}% em relação ao total de CNPJs monitorados`}
        />
      </div>

      <div className="mt-6 rounded-2xl border border-agnes-border bg-agnes-bg-surface p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="min-w-[320px] flex-1">
            <SearchInput
              value={busca}
              onValueChange={setBusca}
              onSearch={() => useCndtStore.getState().buscar()}
              placeholder="Pesquisar por empregador ou código de controle"
            />
          </div>
          {podeEditar && (
            <>
              <div className="min-w-[240px]">
                <AgnesSelect value={syncCnpj} onValueChange={setSyncCnpj}>
                  <AgnesSelectTrigger className={SELECT_TRIGGER}>
                    <AgnesSelectValue placeholder="Empregador para sincronizar" />
                  </AgnesSelectTrigger>
                  <AgnesSelectContent>
                    {empregadores.map((e) => (
                      <AgnesSelectItem key={e.cnpjFilial} value={e.cnpjFilial}>
                        {e.company || e.fantasyName || ''}
                      </AgnesSelectItem>
                    ))}
                  </AgnesSelectContent>
                </AgnesSelect>
              </div>
              <AgnesButton
                variant="primary"
                disabled={!syncCnpj || sincronizando}
                onClick={() => useCndtStore.getState().sincronizar(syncCnpj)}
                startIcon={<RefreshCw className="h-4 w-4" />}
              >
                {sincronizando ? 'Sincronizando...' : 'Sincronizar'}
              </AgnesButton>
            </>
          )}
          <CndtFiltro />
          <ColumnsMenu
            columns={columns}
            hiddenColumnKeys={hiddenColumnKeys}
            onToggleColumn={toggleColumn}
            data-qa="cndt-colunas"
          />
          <ExportButton
            variant="outlined"
            startIcon={<Download className="h-4 w-4" />}
            disabled={exportando}
            onClick={() => useCndtStore.getState().exportar()}
          >
            Exportar
          </ExportButton>
        </div>
        <TableWrapper className="mt-4">
          <AgnesDataTable<Cndt>
            columns={columns}
            hiddenColumnKeys={hiddenColumnKeys}
            data={lista}
            loading={carregando}
            rowKey={(r) => String(r.id)}
            sort={{ sortBy, sortOrder, onSortChange: setSort }}
            pagination={{
              page,
              pageSize: size,
              total,
              serverSide: true,
              onPageChange: (p: number) => useCndtStore.getState().setPage(p),
              onPageSizeChange: (s: number) => useCndtStore.getState().setSize(s),
            }}
          />
        </TableWrapper>
      </div>

      <AdicionarCndt
        open={modal}
        onClose={() => {
          setModal(false);
          setEditarLinha(null);
        }}
        editar={editarLinha}
      />
      <AgnesModal
        open={!!histId}
        title="Histórico da CNDT"
        maxWidth="lg"
        onClose={() => setHistId(null)}
      >
        <AgnesDataTable<Any>
          columns={[
            { key: 'status', header: 'Situação' },
            { key: 'codigo', header: 'Código' },
            { key: 'totalProcessos', header: 'Nº processos' },
            { key: 'dataEmissao', header: 'Emissão' },
            { key: 'dataValidade', header: 'Validade' },
          ]}
          data={historico}
          rowKey={(r: Any) => String(r.id)}
        />
      </AgnesModal>
    </>
  );
}
